import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { uniqBy } from 'lodash-es'

import { fetchRest } from '@/lib/hook/tanstackQuery'
import { sortDepartement } from '@/lib/getSortDepartement'
import SETTINGS from '@/src/settings'

const positionBrand = [
  { id: 579, position: 5 },
  { id: 536, position: 3 },
  { id: 364, position: 17 },
  { id: 1700, position: 1 },
  { id: 1771, position: 7 },
  { id: 332, position: 4 },
  { id: 366, position: 8 },
  { id: 1857, position: 9 },
  { id: 703, position: 10 },
  { id: 335, position: 11 },
  { id: 430, position: 12 },
  { id: 1880, position: 13 },
  { id: 334, position: 14 },
  { id: 749, position: 15 },
  { id: 685, position: 16 },
  { id: 762, position: 2 },
  { id: 360, position: 18 },
  { id: 1761, position: 19 },
  { id: 1776, position: 20 },
  { id: 1877, position: 21 },
  { id: 1889, position: 22 },
  { id: 760, position: 23 },
  { id: 438, position: 24 },
  { id: 545, position: 25 },
  { id: 508, position: 26 },
  { id: 684, position: 27 },
  { id: 423, position: 28 },
  { id: 577, position: 29 },
  { id: 739, position: 30 },
  { id: 391, position: 31 },
  { id: 436, position: 32 },
  { id: 1868, position: 33 },
  { id: 1792, position: 34 },
  { id: 1858, position: 6 },
  { id: 1795, position: 35 },
  { id: 601, position: 36 },
]

export type BrandItem = {
  variant_id: string
  variant: string
  feature_id?: string | null
  alpha?: string | null
  seo_name?: string | null
  number?: boolean | null
  position?: number | null
  image_pair?: any
  /** Vendue sur les 12 derniers mois (parametre recent_sales de l'endpoint brands) */
  recently_sold?: boolean | null
  /** Rang parmi les marques les plus commandees (1 = la plus commandee), null hors vitrine */
  top_rank?: number | null
  [key: string]: any
}

export type TabAlphaItem = { title: string; number: number; columns: number }

export type AllBrandsData = { brands: BrandItem[]; tabAlpha: TabAlphaItem[] }

/**
 * Reproduit fidelement la transfo du resolver getAllBrands (positionBrand,
 * sortDepartement, calcul tabAlpha) cote front.
 */
export const formatAllBrands = (featureBrand: any): AllBrandsData => {
  const idBrand = SETTINGS.attributesCodeUse.brandId
  const brandsFormated = featureBrand?.variants ?? []
  let position = 0
  const brands = uniqBy<any>(brandsFormated, 'variant_id')
    .map((brand: any) => {
      const firstLetter = brand.variant?.substr(0, 1)?.toUpperCase()
      return {
        alpha: isNaN(Number(firstLetter)) ? firstLetter : '0-9',
        feature_id: idBrand,
        number: !!brand.number_product,
        position:
          brand.image !== null && !!brand?.image_pair
            ? brand.position > 0
              ? brand.position
              : positionBrand.find(p => p.id === brand.variant_id)?.position ?? (position += 1)
            : 0,
        ...brand,
      }
    })
    .filter((brand: any) => !!brand.number)
    .sort(sortDepartement)

  const numberColumns = 3
  const sizeBrandToColumns = Math.floor(brands.length / numberColumns)
  let countBrandColumns = 0
  let nunberColumnsSelect = 1

  const tabAlpha = [...new Set<string>(brands.map((i: any) => i.alpha))]
    .sort((a, b) => {
      if (a > b) return 1
      if (a < b) return -1
      return 0
    })
    .map(alpha => {
      const countBrand = brands.filter((i: any) => i.alpha === alpha).length
      countBrandColumns += countBrand

      const returnTabAlpha = {
        title: alpha,
        number: countBrand,
        columns: nunberColumnsSelect,
      }
      if (countBrandColumns >= sizeBrandToColumns && nunberColumnsSelect < numberColumns) {
        nunberColumnsSelect += 1
        countBrandColumns = 0
      }

      return returnTabAlpha
    })

  return { brands: brands as BrandItem[], tabAlpha }
}

export const allBrandsQueryKey = (displayLeader = false) =>
  ['allBrands', displayLeader] as const

export const fetchAllBrands = async (
  displayLeader = false,
  locale?: string,
  baseUrl?: string
): Promise<AllBrandsData> => {
  const data = await fetchRest<any>({
    endpoint: 'brands',
    queriesParams: {
      use_caching: true,
      number_product: true,
      recent_sales: true,
      ...(displayLeader ? { display_leader: true } : {}),
    },
    locale,
    baseUrl,
  })
  return formatAllBrands(data)
}

const useAllBrands = (displayLeader = false) => {
  const router = useRouter()
  const locale = (router.query.locale as string) || 'fr'
  return useQuery({
    queryKey: allBrandsQueryKey(displayLeader),
    queryFn: () => fetchAllBrands(displayLeader, locale),
  })
}

/** Cle de comparaison d'un nom de marque (le filtre Marques porte le nom, pas l'identifiant) */
export const brandNameKey = (name?: string | null): string => (name ?? '').trim().toLowerCase()

/** Ordre alphabetique d'un nom de marque : accents et casse ignores, « 3M » avant « 10 Doigts » */
export const compareBrandNames = (a?: string | null, b?: string | null): number =>
  (a ?? '').localeCompare(b ?? '', 'fr', { sensitivity: 'base', numeric: true })

/**
 * Rang des marques les plus commandees (top_rank, 50 premieres), par nom de marque : le filtre
 * Marques porte le nom, pas l'identifiant. Meme requete que la page Marques et le menu : deja en
 * cache le plus souvent.
 */
export const useTopBrandRanks = (): Map<string, number> => {
  const { data } = useAllBrands()
  return useMemo(
    () =>
      new Map(
        (data?.brands ?? [])
          .filter(brand => !!brand.top_rank)
          .map(brand => [brandNameKey(brand.variant), Number(brand.top_rank)] as const)
      ),
    [data]
  )
}

export default useAllBrands
